import React from 'react';
import { Box, Container, Typography, Paper, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import SecurityIcon from '@mui/icons-material/Security';
import ComputerIcon from '@mui/icons-material/Computer';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import HandshakeIcon from '@mui/icons-material/Handshake';
import TimelineIcon from '@mui/icons-material/Timeline';
import SpeedIcon from '@mui/icons-material/Speed';
import GraystoneGroupSolutionsLogo from '../components/GraystoneGroupSolLogo';

const MotionTypography = motion(Typography);

const Commercial: React.FC = () => {
  const services = [
    {
      title: 'Executive Search',
      icon: <BusinessCenterIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: 'Specialized executive placement services for leadership roles across organizations.',
    },
    {
      title: 'Cyber Security',
      icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: 'Proactive and reactive cybersecurity talent solutions.',
    },
    {
      title: 'Information Technology',
      icon: <ComputerIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: 'Comprehensive IT staffing solutions across all technology domains.',
    },
    {
      title: 'Financial & Accounting',
      icon: <AccountBalanceIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: 'Expert placement services for finance and accounting professionals.',
    },
    {
      title: 'Corporate Operations',
      icon: <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: 'Strategic staffing solutions for operational excellence.',
    },
  ];

  const values = [
    {
      title: 'Transparency',
      icon: <VerifiedUserIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      title: 'Trust',
      icon: <HandshakeIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      title: 'Predictability',
      icon: <SpeedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      title: 'Consistency',
      icon: <TimelineIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
  ];

  return (
    <Box>
      {/* Commercial Hero Component */}
      <Box
        sx={{
          width: '100vw',
          position: 'relative',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw',
          textAlign: 'center',
          pt: { xs: 2, sm: 4, md: 6 },
          px: { xs: 2, sm: 4 },
          color: 'text.primary',
          overflow: 'hidden',
          overflowX: 'hidden',
          background: `linear-gradient(#0d0d0d, rgba(7, 7, 7, 0.6)), url('/images/commercial-growth.jpg') center/cover no-repeat`,
          minHeight: { xs: 200, sm: 240, md: 280 },
          mt: 0,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          justtifyContent: 'center', 
        }}
      >
        <Box
          sx={{ width: '40vw', minWidth: 274, maxWidth: 274, alignSelf: 'center',}}
          >
          <GraystoneGroupSolutionsLogo />
        </Box>
        <MotionTypography 
          variant="h7" 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          sx={{ 
            fontSize: { xs: '1.0rem', sm: '1.25rem', md: '1.5rem' },
            // maxWidth: { xs: '100%', sm: '80%', md: '60%' },
            margin: '20px',
            lineHeight: 1.4,
            position: 'relative',
            zIndex: 2,
            color: 'rgba(255,255,255,0.85)',
            textShadow: '0 2px 8px rgba(0,0,0,0.25)',
            fontWeight: 300,
            fontStyle: 'italic',
            px: { xs: 2, sm: 4, md: 6, lg: 8, xl: 10 },
          }}
        >
          Relationship-driven recruiting firm wholly dedicated to delivering best-in-class talent services to small, mid-sized and enterprise organizations within the Private Sector        
        </MotionTypography>
      </Box>

      <Box sx={{ bgcolor: 'background.paper', pt: { xs: 4, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ mb: 6 }}
          >
            Our Capabilities
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={6} lg={4} key={index}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  {service.icon}
                  <Typography variant="h6" sx={{ my: 2 }}>
                    {service.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {service.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ bgcolor: 'background.paper', pt: { xs: 4 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ mb: 4 }}
          >
            About Us
          </Typography>

          <Typography
            variant="body1"
            sx={{
              maxWidth: '900px',
              mx: 'auto',
              textAlign: 'center',
              mb: 3,
              lineHeight: 1.8,
            }}
          >
            With over a decade of experience in the recruitment industry, our team devotes themselves to a seamless, optimized, and predictable hiring process to ensure highly beneficial outcomes to our customers and candidates. For our customers, our approach is simple: work with great people at great companies to help them bridge talent gaps with the most qualified and culturally aligned candidates the market has to offer.
          </Typography>

          <Typography
            variant="body1"
            sx={{
              maxWidth: '900px',
              mx: 'auto',
              textAlign: 'center',
              mb: 3,
              lineHeight: 1.8,
            }}
          >
            Our ability to fully understand your business needs, translate those needs into candidate qualifications and execute on identifying the right resources through our talent engagement process is what makes us different. Leveraging our experience across hundreds of clients, thousands of placements, and tens of thousands of interviews, we have encapsulated the most efficient and effective hiring practices to ensure predictability and consistency.
          </Typography>

          <Typography
            variant="body1"
            sx={{
              maxWidth: '900px',
              mx: 'auto',
              textAlign: 'center',
              lineHeight: 1.8,
            }}
          >
            For our job seekers, our commitment is to provide the best candidate experience we possibly can. In an otherwise client-driven field, we understand the importance and value our candidates bring to the table, so we emphasize our priority of your wants and needs to ensure we are introducing you to the best opportunity... for YOU.
          </Typography>
        </Container>
      </Box>

      <Box sx={{ bgcolor: 'background.paper', py: { xs: 4} }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ mb: 6 }}
          >
            Core Values
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {values.map((value, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  {value.icon}
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    {value.title}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Commercial; 